/**
 * iter-121.170 — FileBrowserStep
 *
 * The "Pick files" step for cloud-storage connector drivers (Amazon S3,
 * SFTP, and any future driver that implements `browse(cfg, path)` on
 * the backend). Renders a tree-style folder view with lazy-expand:
 * clicking a folder fires an API call for that path's contents on
 * first expand only, then caches.
 *
 * IMPORTANT: The tree is flattened into a linear list at render time
 * (see `flattenTree`). A recursive component would trigger a stack
 * overflow in the dev-only @emergentbase/visual-edits Babel plugin,
 * which infinite-recurses through self-referential JSX definitions.
 *
 * Selection state lives in the same `selected` array the TablesStep
 * uses ({schema:"", name:<file_path>, incremental_col:""}) so
 * downstream wizard steps (relationships, mapping, ingest) work
 * verbatim with zero per-driver branching.
 */
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    ChevronRight, Folder, FolderOpen, FileText, FileSpreadsheet,
    FileJson, FileType, FolderTree, Loader2,
} from "lucide-react";

function humaniseBytes(n) {
    if (n === null || n === undefined) return "";
    if (n < 1024) return n + " B";
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
    if (n < 1024 * 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + " MB";
    return (n / (1024 * 1024 * 1024)).toFixed(2) + " GB";
}

function humaniseTimestamp(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        return d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
    } catch (_e) { return ""; }
}

function iconForKind(kind) {
    if (kind === "csv" || kind === "xlsx") return FileSpreadsheet;
    if (kind === "json") return FileJson;
    if (kind === "parquet") return FileType;
    return FileText;
}

/**
 * Walk the tree iteratively and produce a flat list of rows to render.
 * Each row carries its `depth` (for indentation), the raw `item`, plus
 * a virtual "loading" or "empty" placeholder for expanded folders whose
 * children haven't loaded yet.
 *
 * iter-121.173 — Unsupported files (type=file && supported=false) are
 * dropped here rather than in the driver so the backend response stays
 * consistent for any consumer that might want to show them. A folder
 * that contained ONLY unsupported files therefore renders as an empty
 * folder ("(empty folder)") — which is the correct signal to the user
 * that nothing there is ingestable.
 */
function flattenTree(rootItems, filesByPath, expandedPaths) {
    const out = [];
    const stack = [];
    // Filter root items — hide unsupported files, keep every folder.
    const filteredRoot = rootItems.filter(
        (it) => it.type !== "file" || it.supported
    );
    for (let i = filteredRoot.length - 1; i >= 0; i--) {
        stack.push({ item: filteredRoot[i], depth: 0 });
    }
    while (stack.length) {
        const { item, depth } = stack.pop();
        out.push({ kind: "row", item, depth });
        if (item.type === "folder" && expandedPaths[item.path]) {
            const children = filesByPath[item.path];
            if (children === undefined) {
                out.push({ kind: "loading", path: item.path, depth: depth + 1 });
            } else {
                // Apply the same file-filter to children.
                const visible = children.filter(
                    (c) => c.type !== "file" || c.supported
                );
                if (visible.length === 0) {
                    out.push({ kind: "empty", path: item.path, depth: depth + 1 });
                } else {
                    for (let i = visible.length - 1; i >= 0; i--) {
                        stack.push({ item: visible[i], depth: depth + 1 });
                    }
                }
            }
        }
    }
    return out;
}

function FolderRow(props) {
    const {
        item, depth, expanded, children,
        toggleFolder, toggleFolderSelection, selected,
    } = props;
    const supportedFiles = (children || []).filter(
        (c) => c.type === "file" && c.supported
    );
    let selectedInFolder = 0;
    if (supportedFiles.length > 0) {
        const paths = new Set(supportedFiles.map((f) => f.path));
        for (const s of selected) {
            if ((s.schema || "") === "" && paths.has(s.name)) selectedInFolder += 1;
        }
    }
    const allSelected = supportedFiles.length > 0
        && selectedInFolder === supportedFiles.length;
    const chevronCls = "h-3.5 w-3.5 transition-transform "
        + (expanded ? "rotate-90" : "");
    const selectBtnCls = "shrink-0 text-[10px] font-mono uppercase "
        + "tracking-wider hover:underline "
        + (allSelected ? "text-primary" : "text-muted-foreground hover:text-primary");

    return (
        <div
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/30 border-b border-border last:border-0"
            style={{ paddingLeft: 12 + depth * 20 }}
            data-testid={"wiz-folder-row-" + item.path}
        >
            <button
                onClick={() => toggleFolder(item.path)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
                data-testid={"wiz-folder-toggle-" + item.path}
            >
                <ChevronRight className={chevronCls} />
                {expanded
                    ? <FolderOpen className="h-3.5 w-3.5 text-primary" />
                    : <Folder className="h-3.5 w-3.5 text-primary" />}
                <span className="font-mono text-sm truncate">{item.name}</span>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">/</span>
            </button>
            {children && supportedFiles.length > 0 && (
                <button
                    type="button"
                    onClick={() => toggleFolderSelection(item.path)}
                    className={selectBtnCls}
                    data-testid={"wiz-folder-select-all-" + item.path}
                >
                    {allSelected ? "Deselect all" : "Select " + supportedFiles.length}
                </button>
            )}
        </div>
    );
}

function FileRow(props) {
    const { item, depth, isSelected, busy, toggleFile } = props;
    const Icon = iconForKind(item.kind);
    const supported = !!item.supported;
    const rowCls = "w-full flex items-center gap-2 px-3 py-2 border-b border-border last:border-0 "
        + (supported ? "hover:bg-muted/30" : "opacity-55");
    const iconCls = "h-3.5 w-3.5 shrink-0 "
        + (supported ? "text-muted-foreground" : "text-muted-foreground/60");
    return (
        <div
            className={rowCls}
            style={{ paddingLeft: 12 + depth * 20 }}
            data-testid={"wiz-file-row-" + item.path}
        >
            <Checkbox
                checked={isSelected}
                disabled={!supported || busy}
                onCheckedChange={() => supported && toggleFile(item.path)}
                data-testid={"wiz-file-" + item.path}
            />
            <Icon className={iconCls} />
            <span className="font-mono text-sm truncate flex-1 min-w-0" title={item.path}>
                {item.name}
            </span>
            {item.ext && (
                <Badge variant="outline" className="font-mono text-[9px] shrink-0 uppercase">
                    {item.ext}
                </Badge>
            )}
            {!supported && (
                <Badge
                    variant="outline"
                    className="font-mono text-[9px] shrink-0 border-orange-500/50 text-orange-600 dark:text-orange-400"
                >
                    unsupported
                </Badge>
            )}
            {item.size != null && (
                <span className="font-mono text-[10px] text-muted-foreground shrink-0 min-w-[60px] text-right">
                    {humaniseBytes(item.size)}
                </span>
            )}
            {item.last_modified && (
                <span className="font-mono text-[10px] text-muted-foreground shrink-0 min-w-[130px] text-right hidden md:inline">
                    {humaniseTimestamp(item.last_modified)}
                </span>
            )}
        </div>
    );
}

export function FileBrowserStep({ ctx }) {
    const {
        filesByPath, expandedPaths, busy, driver, selected,
        toggleFolder, toggleFile, toggleFolderSelection, isFileSelected,
    } = ctx;
    const rootItems = filesByPath[""];
    const rootLoaded = rootItems !== undefined;
    const headerText = "Pick files to ingest"
        + (driver && driver.name ? " — " + driver.name : "");

    const flatRows = React.useMemo(() => {
        if (!rootLoaded) return [];
        return flattenTree(rootItems, filesByPath, expandedPaths);
    }, [rootItems, filesByPath, expandedPaths, rootLoaded]);

    // iter-121.173 — After filtering out unsupported files in
    // flattenTree, a root that only contained e.g. .png files renders
    // 0 rows. Treat that the same as a truly empty bucket so the user
    // gets the "No files found" empty-state instead of a blank table.
    const isEmpty = rootLoaded && flatRows.length === 0;

    return (
        <div data-testid="wiz-step-files">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="eyebrow">{headerText}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Click a folder to expand. Select individual files or use{" "}
                        <span className="font-mono">Select N</span>{" "}
                        on a folder to pick every supported file inside.
                        Supported formats: CSV, TSV, JSON, JSONL, Excel, Parquet.
                    </div>
                </div>
                <div className="text-[11px] font-mono">
                    {selected.length} selected
                </div>
            </div>

            {busy && !rootLoaded && (
                <div className="text-center py-10 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Loading files…
                </div>
            )}

            {rootLoaded && isEmpty && (
                <div className="border border-border rounded-sm p-6 text-center bg-card">
                    <FolderTree className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm font-semibold mb-1">No files found</div>
                    <div className="text-xs text-muted-foreground">
                        The bucket / directory is empty, or no files match the configured prefix.
                    </div>
                </div>
            )}

            {rootLoaded && !isEmpty && (
                <div className="border border-border rounded-sm divide-y divide-border bg-card max-h-[52vh] overflow-y-auto">
                    {flatRows.map((row) => {
                        if (row.kind === "loading") {
                            return (
                                <div
                                    key={"loading-" + row.path}
                                    className="px-3 py-3 text-xs text-muted-foreground bg-secondary/20"
                                    style={{ paddingLeft: 12 + row.depth * 20 }}
                                >
                                    <Loader2 className="h-3 w-3 animate-spin inline mr-1.5" />
                                    Loading contents…
                                </div>
                            );
                        }
                        if (row.kind === "empty") {
                            return (
                                <div
                                    key={"empty-" + row.path}
                                    className="px-3 py-3 text-xs text-muted-foreground italic bg-secondary/20"
                                    style={{ paddingLeft: 12 + row.depth * 20 }}
                                >
                                    (empty folder)
                                </div>
                            );
                        }
                        const item = row.item;
                        if (item.type === "folder") {
                            return (
                                <FolderRow
                                    key={"f-" + item.path}
                                    item={item}
                                    depth={row.depth}
                                    expanded={!!expandedPaths[item.path]}
                                    children={filesByPath[item.path]}
                                    toggleFolder={toggleFolder}
                                    toggleFolderSelection={toggleFolderSelection}
                                    selected={selected}
                                />
                            );
                        }
                        return (
                            <FileRow
                                key={"x-" + item.path}
                                item={item}
                                depth={row.depth}
                                isSelected={isFileSelected(item.path)}
                                busy={busy}
                                toggleFile={toggleFile}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default FileBrowserStep;
